import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit, Trash2, Clock, DollarSign, Check } from 'lucide-react';
import { 
  AddonsLibraryItem, 
  getAddonsLibraryItems, 
  createAddonsLibraryItem, 
  updateAddonsLibraryItem, 
  deleteAddonsLibraryItem 
} from '../lib/addonsLibrary';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';

interface AddonsLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'manage' | 'select';
  serviceId?: string;
  onSelect?: (addons: AddonsLibraryItem[]) => void;
}

interface AddonsFormData {
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
}

export default function AddonsLibraryModal({
  isOpen,
  onClose,
  mode = 'manage',
  serviceId,
  onSelect
}: AddonsLibraryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [addons, setAddons] = useState<AddonsLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState<AddonsLibraryItem | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectionOrder, setSelectionOrder] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<AddonsFormData>({
    name: '',
    description: '',
    price: 0,
    duration_minutes: 0
  });

  useEffect(() => {
    if (isOpen) {
      loadAddons();
      setSelectedAddons([]); // Reset selection when modal opens
      setSelectionOrder([]); // Reset selection order when modal opens
    }
  }, [isOpen]);

  const loadAddons = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);
      const addonsData = await getAddonsLibraryItems(selectedBusiness.id);
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading addons library:', err);
      setError(err instanceof Error ? err.message : 'Failed to load addons library');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAddon = async () => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Add-on name is required');
      return;
    }

    try {
      setLoading(true);
      await createAddonsLibraryItem({
        business_id: selectedBusiness.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: formData.price,
        duration_minutes: formData.duration_minutes
      });

      toast.success('Add-on created successfully');
      setShowCreateForm(false);
      resetForm();
      loadAddons();
    } catch (err) {
      console.error('Error creating addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddon = async () => {
    if (!editingAddon) return;

    if (!formData.name.trim()) {
      toast.error('Add-on name is required');
      return;
    }

    try {
      setLoading(true);
      await updateAddonsLibraryItem(editingAddon.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: formData.price,
        duration_minutes: formData.duration_minutes
      });

      toast.success('Add-on updated successfully');
      setEditingAddon(null);
      resetForm();
      loadAddons();
    } catch (err) {
      console.error('Error updating addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddon = async (addonId: string) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteAddonsLibraryItem(addonId);
      toast.success('Add-on deleted successfully');
      loadAddons();
    } catch (err) {
      console.error('Error deleting addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddon = (addon: AddonsLibraryItem) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      description: addon.description || '',
      price: addon.price,
      duration_minutes: addon.duration_minutes
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      duration_minutes: 0
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingAddon(null);
    resetForm();
  };

  const toggleAddonSelection = (addonId: string) => {
    const isCurrentlySelected = selectedAddons.includes(addonId);
    
    if (isCurrentlySelected) {
      // Remove from selection
      setSelectedAddons(prev => prev.filter(id => id !== addonId));
      setSelectionOrder(prev => prev.filter(id => id !== addonId));
    } else {
      // Add to selection
      setSelectedAddons(prev => [...prev, addonId]);
      setSelectionOrder(prev => [...prev, addonId]);
    }
    
    // Debug logging
    console.log('Toggling addon:', addonId);
    console.log('Currently selected:', selectedAddons);
    console.log('Selection order:', selectionOrder);
  };

  const handleAddToService = () => {
    // Return add-ons in the order they were selected
    const selectedAddonItems = selectionOrder
      .map(id => addons.find(addon => addon.id === id))
      .filter(addon => addon !== undefined) as AddonsLibraryItem[];
    onSelect?.(selectedAddonItems);
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'select' ? 'Select Add-on' : 'Add-ons Library'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'select' 
                ? 'Choose an add-on template to add to your service'
                : 'Manage your reusable add-on templates'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-xl transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}

          {/* Create/Edit Form */}
          {showCreateForm && (
            <div className="mb-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingAddon ? 'Edit Add-on' : 'Create New Add-on'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter add-on name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingAddon ? handleUpdateAddon : handleCreateAddon}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingAddon ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          )}

          {/* Add New Button */}
          {!showCreateForm && (
            <div className="mb-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Add-on
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && !showCreateForm && (
            <div className="py-12 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-2xl bg-gray-100/50">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                Loading add-ons...
              </div>
            </div>
          )}

          {/* Add-ons List */}
          {!loading && !showCreateForm && (
            <div>
              {addons.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No add-ons yet</h3>
                  <p className="text-gray-500 mb-4">
                    {mode === 'select' 
                      ? 'No add-on templates available. Create some templates first.'
                      : 'Create your first add-on template to get started.'
                    }
                  </p>
                  {mode === 'manage' && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Add-on
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {addons.map((addon) => (
                    <div
                      key={addon.id}
                      className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 flex-1">{addon.name}</h4>
                        {mode === 'select' ? (
                          <div className="flex items-center space-x-2">
                            {selectedAddons.includes(addon.id) && (
                              <span className="text-xs font-bold text-purple-600 bg-purple-100 rounded-full w-5 h-5 flex items-center justify-center">
                                {selectionOrder.indexOf(addon.id) + 1}
                              </span>
                            )}
                            <button
                              onClick={() => toggleAddonSelection(addon.id)}
                              className={`p-1 rounded transition-colors ${
                                selectedAddons.includes(addon.id)
                                  ? 'text-purple-600 bg-purple-100'
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEditAddon(addon)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAddon(addon.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {addon.description && (
                        <p className="text-sm text-gray-600 mb-2">{addon.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-green-600">
                          <DollarSign className="h-4 w-4 mr-1" />
                          ${addon.price.toFixed(2)}
                        </div>
                        <div className="flex items-center text-blue-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {formatDuration(addon.duration_minutes)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer for Select Mode */}
        {mode === 'select' && !showCreateForm && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {selectedAddons.length} add-on{selectedAddons.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToService}
                disabled={selectedAddons.length === 0}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Service
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
